<script setup>
  import { computed, onMounted, ref, watch } from "vue";
  import { useStore } from "vuex";

  import {
    Globe,
    Power,
    Loader2,
    TriangleAlert,
    CheckCircle2,
    X,
    Copy,
    Lock,
    ShieldCheck,
    RefreshCw,
    Network,
    Sparkles,
    Search,
    Phone,
    MapPin,
    Mail,
    Clock,
    Share2,
    Save,
  } from "lucide-vue-next";

  import { useGlobalToast } from "@/utils/useGlobalToast";

  const props = defineProps({
    activePage: {
      type: String,
      default: "website-settings",
    },
  });

  const { showToast } = useGlobalToast();
  const store = useStore();

  /* =========================================
     ACTIVE TAB MANAGEMENT
  ========================================= */
  const activeTab = ref("domain"); // 'domain' | 'seo' | 'contact'

  const syncActiveTabFromProps = () => {
    if (props.activePage === "seo-settings") {
      activeTab.value = "seo";
    } else if (props.activePage === "contact-details") {
      activeTab.value = "contact";
    } else if (props.activePage === "domain-settings") {
      activeTab.value = "domain";
    }
  };

  watch(() => props.activePage, syncActiveTabFromProps, { immediate: true });

  /* =========================================
     DOMAIN & HOSTING STATE
  ========================================= */
  const domain = ref("");
  const loading = ref(false);
  const confirmModal = ref(false);
  const actionType = ref("");
  const sslActivated = ref(false);
  const SERVER_IP = "206.189.129.218";

  // DNS Guide Tab State ('a' or 'cname')
  const recordType = ref("a");

  // Interactive UI Simulation State
  const checkingDns = ref(false);
  const dnsVerified = ref(false);

  /* =========================================
     SEO & CONTACT FORM STATE
  ========================================= */
  const isSavingSeo = ref(false);
  const isSavingContact = ref(false);

  const seoForm = ref({
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    ogImage: "",
    googleAnalyticsId: "",
    facebookPixelId: "",
  });

  const contactForm = ref({
    primaryPhone: "",
    secondaryPhone: "",
    whatsappNumber: "",
    email: "",
    officeHours: "",
    address: "",
    village: "",
    city: "",
    district: "",
    state: "",
    country: "India",
    pincode: "",
    googleMapLink: "",
  });

  /* =========================================
     STORE
  ========================================= */
  const website = computed(() => store.getters.getWebsiteDetails || store.state.websiteDetails);

  /* =========================================
     POPULATE LOCAL STATE FROM STORE
  ========================================= */
  const loadFormData = () => {
    const w = website.value || {};

    if (w.customDomain) {
      domain.value = w.customDomain;
      sslActivated.value = true;
      dnsVerified.value = true;
    }

    seoForm.value = {
      seoTitle: w.seoTitle || w.schoolName || "",
      seoDescription: w.seoDescription || w.description || w.tagline || "",
      seoKeywords: Array.isArray(w.seoKeywords)
        ? w.seoKeywords.join(", ")
        : (typeof w.seoKeywords === "string" ? w.seoKeywords : ""),
      ogImage: w.ogImage || w.logo || "",
      googleAnalyticsId: w.googleAnalyticsId || "",
      facebookPixelId: w.facebookPixelId || "",
    };

    contactForm.value = {
      primaryPhone: w.primaryPhone || "",
      secondaryPhone: w.secondaryPhone || "",
      whatsappNumber: w.whatsappNumber || "",
      email: w.email || "",
      officeHours: w.officeHours || "Mon–Sat · 8:00 AM–3:00 PM",
      address: w.address || "",
      village: w.village || "",
      city: w.city || "",
      district: w.district || "",
      state: w.state || "",
      country: w.country || "India",
      pincode: w.pincode || "",
      googleMapLink: w.googleMapLink || "",
    };
  };

  onMounted(async () => {
    try {
      await store.dispatch("fetchWebsiteDetails");
      loadFormData();
    } catch (err) {
      console.error("Failed to fetch website settings:", err);
    }
  });

  watch(website, () => {
    loadFormData();
  });

  /* =========================================
     COPY HELPERS
  ========================================= */
  const copyIP = async () => {
    try {
      await navigator.clipboard.writeText(SERVER_IP);
      showToast("Server IP copied to clipboard!", "success");
    } catch (err) {
      console.error(err);
    }
  };

  const copyDefaultUrl = async () => {
    try {
      const url = `${website.value?.slug || "instance"}.educanium.com`;
      await navigator.clipboard.writeText(url);
      showToast("Target URL copied to clipboard!", "success");
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================================
     SIMULATE DNS CHECK
  ========================================= */
  const checkDNSConfig = () => {
    if (!domain.value) return;
    checkingDns.value = true;
    setTimeout(() => {
      checkingDns.value = false;
      dnsVerified.value = true;
      showToast("DNS records found! You are ready to connect.", "success");
    }, 1200);
  };

  /* =========================================
     MODAL CONTROLS
  ========================================= */
  const openConfirmation = (type) => {
    actionType.value = type;
    confirmModal.value = true;
  };

  const closeModal = () => {
    confirmModal.value = false;
  };

  /* =========================================
     SAVE DOMAIN + AUTO SSL
  ========================================= */
  const saveDomain = async () => {
    try {
      loading.value = true;
      await store.dispatch("connectCustomDomain", domain.value);

      showToast(
        website.value?.customDomain
          ? "Domain updated successfully."
          : "Domain connected successfully.",
        "success"
      );

      try {
        const sslRes = await store.dispatch("canIssueCert", domain.value);
        sslActivated.value = true;
        showToast(
          sslRes.message || "Security certificate (SSL) enabled! HTTPS is live.",
          "success"
        );
      } catch (sslErr) {
        showToast(
          sslErr.response?.data?.message || "Domain saved, but security certificate setup failed.",
          "error"
        );
      }

      await store.dispatch("fetchWebsiteDetails");
      closeModal();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to connect domain",
        "error"
      );
    } finally {
      loading.value = false;
    }
  };

  /* =========================================
     WEBSITE TOGGLE
  ========================================= */
  const toggleWebsite = async () => {
    try {
      loading.value = true;
      await store.dispatch("toggleWebsiteStatus");
      await store.dispatch("fetchWebsiteDetails");

      showToast(
        website.value?.websiteStatus === "active"
          ? "Website is now live online."
          : "Website turned off successfully.",
        "success"
      );

      closeModal();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to update website status",
        "error"
      );
    } finally {
      loading.value = false;
    }
  };

  /* =========================================
     SAVE SEO SETTINGS
  ========================================= */
  const saveSeoSettings = async () => {
    isSavingSeo.value = true;
    try {
      const keywordsArray = seoForm.value.seoKeywords
        ? seoForm.value.seoKeywords.split(",").map((k) => k.trim()).filter(Boolean)
        : [];

      const payload = {
        seoTitle: seoForm.value.seoTitle.trim(),
        seoDescription: seoForm.value.seoDescription.trim(),
        seoKeywords: keywordsArray,
        ogImage: seoForm.value.ogImage.trim(),
        googleAnalyticsId: seoForm.value.googleAnalyticsId.trim(),
        facebookPixelId: seoForm.value.facebookPixelId.trim(),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await store.dispatch("fetchWebsiteDetails");
      showToast("SEO & analytics configuration saved successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to save SEO settings", "error");
    } finally {
      isSavingSeo.value = false;
    }
  };

  /* =========================================
     SAVE CONTACT DETAILS
  ========================================= */
  const saveContactDetails = async () => {
    isSavingContact.value = true;
    try {
      const payload = {
        primaryPhone: contactForm.value.primaryPhone.trim(),
        secondaryPhone: contactForm.value.secondaryPhone.trim(),
        whatsappNumber: contactForm.value.whatsappNumber.trim(),
        email: contactForm.value.email.trim(),
        officeHours: contactForm.value.officeHours.trim(),
        address: contactForm.value.address.trim(),
        village: contactForm.value.village.trim(),
        city: contactForm.value.city.trim(),
        district: contactForm.value.district.trim(),
        state: contactForm.value.state.trim(),
        country: contactForm.value.country.trim(),
        pincode: contactForm.value.pincode.trim(),
        googleMapLink: contactForm.value.googleMapLink.trim(),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await store.dispatch("fetchWebsiteDetails");
      showToast("Campus contact & location updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to save contact details", "error");
    } finally {
      isSavingContact.value = false;
    }
  };

  /* =========================================
     CONFIRM ACTION
  ========================================= */
  const confirmAction = async () => {
    if (actionType.value === "domain") {
      await saveDomain();
    }
    if (actionType.value === "website") {
      await toggleWebsite();
    }
  };
</script>

<template>
  <div
    class="w-full text-slate-900 dark:text-[#F1F5F9] font-sans antialiased pb-16 px-3 sm:px-6 max-w-7xl mx-auto space-y-8"
  >
    <!-- TOP PAGE HEADER BANNER -->
    <div
      class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#111C30] p-6 sm:p-8 rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs"
    >
      <div>
        <div
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 text-xs font-extrabold tracking-wider uppercase mb-3 border border-emerald-200/60 dark:border-emerald-800/40"
        >
          <Sparkles :size="13" />
          <span>Educanium Network</span>
        </div>
        <h1
          class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-[#F1F5F9]"
        >
          Configuration Studio
        </h1>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-1 max-w-xl">
          Manage your custom domain, search engine optimization, and official school contact details.
        </p>
      </div>

      <!-- Live Quick Status Badge -->
      <div
        class="flex items-center gap-3 bg-slate-50 dark:bg-[#172235] border border-slate-200/80 dark:border-[#263449] p-3.5 rounded-2xl shrink-0"
      >
        <div class="relative flex h-3 w-3">
          <span
            class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-emerald-400'
                : 'bg-rose-400'
            "
          ></span>
          <span
            class="relative inline-flex rounded-full h-3 w-3"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-emerald-500'
                : 'bg-rose-500'
            "
          ></span>
        </div>
        <div>
          <span
            class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] block"
            >Status</span
          >
          <span class="text-xs font-bold capitalize text-slate-800 dark:text-[#F1F5F9]">{{
            website?.websiteStatus === "active"
              ? "Online & Public"
              : "Offline / Hidden"
          }}</span>
        </div>
      </div>
    </div>

    <!-- NAVIGATION TABS -->
    <div class="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 bg-slate-100 dark:bg-[#172235] rounded-2xl border border-slate-200/80 dark:border-[#263449] w-full sm:w-auto overflow-x-auto no-scrollbar">
      <button
        type="button"
        @click="activeTab = 'domain'"
        class="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition active:scale-95 cursor-pointer whitespace-nowrap"
        :class="
          activeTab === 'domain'
            ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-xs'
            : 'text-slate-500 dark:text-[#A6B4C8] hover:text-slate-800 dark:hover:text-white'
        "
      >
        <Globe :size="15" class="text-indigo-600 dark:text-blue-400 shrink-0" />
        <span>Domain & SSL</span>
      </button>

      <button
        type="button"
        @click="activeTab = 'seo'"
        class="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition active:scale-95 cursor-pointer whitespace-nowrap"
        :class="
          activeTab === 'seo'
            ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-xs'
            : 'text-slate-500 dark:text-[#A6B4C8] hover:text-slate-800 dark:hover:text-white'
        "
      >
        <Search :size="15" class="text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span>SEO & Analytics</span>
      </button>

      <button
        type="button"
        @click="activeTab = 'contact'"
        class="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition active:scale-95 cursor-pointer whitespace-nowrap"
        :class="
          activeTab === 'contact'
            ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-xs'
            : 'text-slate-500 dark:text-[#A6B4C8] hover:text-slate-800 dark:hover:text-white'
        "
      >
        <Phone :size="15" class="text-amber-600 dark:text-amber-400 shrink-0" />
        <span>Contact & Location</span>
      </button>
    </div>

    <!-- ========================================================
         TAB 1: DOMAIN & SSL
    ======================================================== -->
    <div v-show="activeTab === 'domain'" class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <!-- LEFT COLUMN: DOMAIN SETTINGS (8 Cols) -->
      <section
        class="lg:col-span-8 bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6"
      >
        <div
          class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-2xl bg-[#0f2423] dark:bg-emerald-950/60 dark:border dark:border-emerald-800/40 text-[#d4af37] flex items-center justify-center shadow-md"
            >
              <Globe :size="20" />
            </div>
            <div>
              <h2
                class="text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight"
              >
                Custom Domain Setup
              </h2>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                Configure DNS records to link your professional URL
              </p>
            </div>
          </div>
        </div>

        <!-- DOMAIN STATUS CARDS -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Free Default Link -->
          <div
            class="bg-slate-50 dark:bg-[#172235] border border-slate-200/70 dark:border-[#263449] rounded-2xl p-4 flex flex-col justify-between"
          >
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]"
              >Free System Subdomain</span
            >
            <div class="flex items-center justify-between gap-2 mt-2">
              <span
                class="text-xs sm:text-sm font-bold text-slate-800 dark:text-[#F1F5F9] break-all"
              >
                {{ website?.slug || "instance" }}.educanium.com
              </span>
              <button
                @click="copyDefaultUrl"
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-[#1F2E47] transition"
                title="Copy Link"
              >
                <Copy :size="15" />
              </button>
            </div>
          </div>

          <!-- Connected Custom Domain -->
          <div
            class="rounded-2xl p-4 flex flex-col justify-between border transition"
            :class="
              website?.customDomain
                ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-200/70 dark:border-emerald-800/50'
                : 'bg-slate-50 dark:bg-[#172235] border-slate-200/70 dark:border-[#263449]'
            "
          >
            <span
              class="text-[10px] font-bold uppercase tracking-wider"
              :class="website?.customDomain ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400 dark:text-[#7E8DA5]'"
              >Custom Branded Domain</span
            >
            <div class="flex items-center justify-between gap-2 mt-2">
              <span
                class="text-xs sm:text-sm font-bold break-all"
                :class="
                  website?.customDomain
                    ? 'text-emerald-900 dark:text-emerald-300'
                    : 'text-slate-400 dark:text-[#7E8DA5]'
                "
              >
                {{ website?.customDomain || "Not Configured" }}
              </span>
              <div v-if="website?.customDomain" class="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                <ShieldCheck :size="15" />
                <span class="text-[10px] font-bold">Active</span>
              </div>
            </div>
          </div>
        </div>

        <!-- DNS SETUP INSTRUCTIONS -->
        <div class="space-y-4 pt-2">
          <div class="flex items-center justify-between">
            <h3 class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] uppercase tracking-wider">
              DNS Configuration Guide
            </h3>
            <div class="flex gap-1 bg-slate-100 dark:bg-[#172235] p-1 rounded-xl">
              <button
                type="button"
                @click="recordType = 'a'"
                class="px-2.5 py-1 text-[11px] font-bold rounded-lg transition"
                :class="
                  recordType === 'a'
                    ? 'bg-white dark:bg-[#111C30] text-slate-800 dark:text-[#F1F5F9] shadow-xs'
                    : 'text-slate-500 hover:text-slate-700 dark:text-[#A6B4C8]'
                "
              >
                A Record (Apex)
              </button>
              <button
                type="button"
                @click="recordType = 'cname'"
                class="px-2.5 py-1 text-[11px] font-bold rounded-lg transition"
                :class="
                  recordType === 'cname'
                    ? 'bg-white dark:bg-[#111C30] text-slate-800 dark:text-[#F1F5F9] shadow-xs'
                    : 'text-slate-500 hover:text-slate-700 dark:text-[#A6B4C8]'
                "
              >
                CNAME (Subdomain)
              </button>
            </div>
          </div>

          <div
            v-if="recordType === 'a'"
            class="bg-slate-50 dark:bg-[#172235] p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] space-y-3"
          >
            <p class="text-xs text-slate-600 dark:text-[#A6B4C8] leading-relaxed">
              To point your root domain (e.g., <strong>yourschool.com</strong>), create an <strong>A Record</strong> pointing to our server IP:
            </p>
            <div class="flex items-center justify-between bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] p-3 rounded-xl font-mono text-xs">
              <div>
                <span class="text-slate-400 dark:text-[#7E8DA5] mr-2">Type:</span><strong>A</strong>
                <span class="text-slate-400 dark:text-[#7E8DA5] mx-2">| Host:</span><strong>@</strong>
                <span class="text-slate-400 dark:text-[#7E8DA5] mx-2">| Value:</span><strong class="text-indigo-600 dark:text-blue-400">{{ SERVER_IP }}</strong>
              </div>
              <button
                @click="copyIP"
                class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1 rounded-md transition"
                title="Copy IP"
              >
                <Copy :size="14" />
              </button>
            </div>
          </div>

          <div
            v-else
            class="bg-slate-50 dark:bg-[#172235] p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] space-y-3"
          >
            <p class="text-xs text-slate-600 dark:text-[#A6B4C8] leading-relaxed">
              To point a subdomain (e.g., <strong>www.yourschool.com</strong>), create a <strong>CNAME Record</strong> pointing to your Educanium slug:
            </p>
            <div class="flex items-center justify-between bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] p-3 rounded-xl font-mono text-xs">
              <div>
                <span class="text-slate-400 dark:text-[#7E8DA5] mr-2">Type:</span><strong>CNAME</strong>
                <span class="text-slate-400 dark:text-[#7E8DA5] mx-2">| Host:</span><strong>www</strong>
                <span class="text-slate-400 dark:text-[#7E8DA5] mx-2">| Value:</span><strong class="text-indigo-600 dark:text-blue-400">{{ website?.slug || "school" }}.educanium.com</strong>
              </div>
              <button
                @click="copyDefaultUrl"
                class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1 rounded-md transition"
                title="Copy Value"
              >
                <Copy :size="14" />
              </button>
            </div>
          </div>
        </div>

        <!-- DOMAIN INPUT FORM -->
        <div class="space-y-3 pt-2">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] block">Connect Domain Name</label>
          <div class="flex flex-col sm:flex-row gap-3">
            <div class="relative flex-1">
              <Globe
                class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5]"
                :size="16"
              />
              <input
                v-model="domain"
                type="text"
                placeholder="e.g. myschool.com or www.myschool.com"
                class="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9] outline-none transition focus:bg-white dark:focus:bg-[#111C30] focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/10"
                @input="dnsVerified = false"
              />
            </div>

            <div class="flex gap-2">
              <button
                v-if="domain && !dnsVerified"
                @click="checkDNSConfig"
                :disabled="checkingDns"
                class="h-12 px-4 rounded-xl text-xs font-bold whitespace-nowrap inline-flex items-center gap-1.5 transition border border-indigo-200 dark:border-blue-900 bg-indigo-50 dark:bg-blue-950/40 text-indigo-700 dark:text-blue-400 hover:bg-indigo-100 dark:hover:bg-blue-950/60 active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                <RefreshCw
                  v-if="checkingDns"
                  class="animate-spin"
                  :size="14"
                />
                <Network
                  v-else
                  :size="14"
                />
                Test DNS
              </button>

              <button
                @click="openConfirmation('domain')"
                :disabled="loading || !domain"
                class="h-12 px-6 rounded-xl text-xs font-bold whitespace-nowrap inline-flex items-center gap-2 transition bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-600/20 active:scale-95 disabled:opacity-50 cursor-pointer flex-1 sm:flex-none justify-center"
              >
                <Loader2
                  v-if="loading"
                  class="animate-spin"
                  :size="14"
                />
                <ShieldCheck
                  v-else
                  :size="14"
                />
                {{ website?.customDomain ? "Update Domain" : "Save Domain" }}
              </button>
            </div>
          </div>
        </div>

        <!-- SSL NOTIFICATION -->
        <div
          v-if="sslActivated"
          class="flex gap-3 p-4 rounded-2xl items-start bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-800/40"
        >
          <div
            class="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-emerald-600 text-white shadow-xs"
          >
            <Lock :size="15" />
          </div>
          <div>
            <h4 class="text-xs font-extrabold text-emerald-950 dark:text-emerald-300">
              SSL Certificate Protected (HTTPS Active)
            </h4>
            <p class="text-xs text-emerald-700 dark:text-emerald-400/90 mt-0.5 leading-relaxed">
              Your domain is securely encrypted with an automated production SSL certificate.
            </p>
          </div>
        </div>
      </section>

      <!-- RIGHT COLUMN: WEBSITE ACCESS TOGGLE (4 Cols) -->
      <section
        class="lg:col-span-4 bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6"
      >
        <div class="border-b border-slate-100 dark:border-[#263449] pb-4">
          <h2 class="text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight">
            Public Availability
          </h2>
          <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Manage global website visibility</p>
        </div>

        <div class="space-y-4">
          <div
            class="p-4 rounded-2xl bg-slate-50 dark:bg-[#172235] border border-slate-200/70 dark:border-[#263449] space-y-2"
          >
            <span class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] block"
              >Current Visibility</span
            >
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
              {{
                website?.websiteStatus === "active"
                  ? "Your school portal is currently live and accessible to the public internet."
                  : "Your portal is offline. Visitors will see a maintenance screen."
              }}
            </p>
          </div>

          <button
            @click="openConfirmation('website')"
            class="w-full h-12 px-5 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-2 transition shadow-md active:scale-95 cursor-pointer"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-rose-600 text-white hover:bg-rose-500 shadow-rose-600/25'
                : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-600/25'
            "
          >
            <Power :size="16" />
            {{
              website?.websiteStatus === "active"
                ? "Turn Off Website"
                : "Turn On Website"
            }}
          </button>
        </div>
      </section>
    </div>

    <!-- ========================================================
         TAB 2: SEO & ANALYTICS
    ======================================================== -->
    <div v-show="activeTab === 'seo'" class="space-y-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <!-- SEO FORM (8 Cols) -->
        <section
          class="lg:col-span-8 bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6"
        >
          <div class="border-b border-slate-100 dark:border-[#263449] pb-4 flex items-center justify-between">
            <div>
              <h2 class="text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight">
                Search Engine Optimization (SEO)
              </h2>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                Optimize how Google and social platforms display your school portal.
              </p>
            </div>
            <button
              type="button"
              @click="saveSeoSettings"
              :disabled="isSavingSeo"
              class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md shadow-indigo-600/20 active:scale-95 transition disabled:opacity-50 cursor-pointer"
            >
              <Loader2 v-if="isSavingSeo" :size="14" class="animate-spin" />
              <Save v-else :size="14" />
              <span>{{ isSavingSeo ? "Saving..." : "Save SEO" }}</span>
            </button>
          </div>

          <div class="space-y-5">
            <!-- Meta Title -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center justify-between">
                <span>Page Meta Title</span>
                <span class="text-[10px] text-slate-400">{{ (seoForm.seoTitle || "").length }}/60 chars</span>
              </label>
              <input
                v-model="seoForm.seoTitle"
                type="text"
                placeholder="e.g. DPS Tezpur · Excellence in CBSE Education"
                class="w-full px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <!-- Meta Description -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center justify-between">
                <span>Meta Description</span>
                <span class="text-[10px] text-slate-400">{{ (seoForm.seoDescription || "").length }}/160 chars</span>
              </label>
              <textarea
                v-model="seoForm.seoDescription"
                rows="3"
                placeholder="A compelling summary shown under your title on Google search results."
                class="w-full px-4 py-2.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            <!-- SEO Keywords -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Search Keywords (Comma Separated)</label>
              <input
                v-model="seoForm.seoKeywords"
                type="text"
                placeholder="e.g. CBSE school, admissions 2026, best school in Tezpur, smart classes"
                class="w-full px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <!-- OpenGraph Social Image URL -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Social Sharing Image URL (OpenGraph)</label>
              <div class="flex gap-2">
                <input
                  v-model="seoForm.ogImage"
                  type="url"
                  placeholder="https://... image displayed when link is shared on WhatsApp/Twitter"
                  class="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <!-- Analytics IDs -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Google Analytics ID</label>
                <input
                  v-model="seoForm.googleAnalyticsId"
                  type="text"
                  placeholder="G-XXXXXXXXXX"
                  class="w-full px-4 py-2.5 text-xs font-mono font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Meta / Facebook Pixel ID</label>
                <input
                  v-model="seoForm.facebookPixelId"
                  type="text"
                  placeholder="123456789012345"
                  class="w-full px-4 py-2.5 text-xs font-mono font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- SERP PREVIEW (4 Cols) -->
        <section class="lg:col-span-4 space-y-6">
          <div class="bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl p-6 shadow-xs space-y-4">
            <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              <Search :size="14" />
              <span>Google SERP Preview</span>
            </div>
            <div class="bg-slate-50 dark:bg-[#172235] p-4 rounded-2xl border border-slate-200/60 dark:border-[#263449] space-y-1.5">
              <div class="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 truncate">
                <span>https://{{ website?.customDomain || (website?.slug + '.educanium.com') }}</span>
              </div>
              <h4 class="text-sm font-bold text-indigo-700 dark:text-blue-400 line-clamp-1 hover:underline cursor-pointer">
                {{ seoForm.seoTitle || website?.schoolName || "School Portal" }}
              </h4>
              <p class="text-xs text-slate-600 dark:text-[#A6B4C8] line-clamp-2 leading-relaxed">
                {{ seoForm.seoDescription || website?.description || "A school built on academic excellence, holistic values, and state-of-the-art campus learning infrastructure." }}
              </p>
            </div>
          </div>

          <div class="bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl p-6 shadow-xs space-y-4">
            <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              <Share2 :size="14" />
              <span>Social Share Preview</span>
            </div>
            <div class="bg-slate-50 dark:bg-[#172235] rounded-2xl border border-slate-200/60 dark:border-[#263449] overflow-hidden">
              <div class="h-32 w-full bg-slate-200 dark:bg-[#1F2E47] flex items-center justify-center overflow-hidden">
                <img
                  v-if="seoForm.ogImage || website?.logo"
                  :src="seoForm.ogImage || website?.logo"
                  alt="OG Preview"
                  class="h-full w-full object-cover"
                />
                <span v-else class="text-xs text-slate-400">No Image Preview</span>
              </div>
              <div class="p-3 space-y-1">
                <span class="text-[10px] uppercase font-bold text-slate-400">{{ website?.customDomain || (website?.slug + '.educanium.com') }}</span>
                <h5 class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] truncate">{{ seoForm.seoTitle || website?.schoolName }}</h5>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- ========================================================
         TAB 3: CONTACT & CAMPUS INFO
    ======================================================== -->
    <div v-show="activeTab === 'contact'" class="bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6">
      <div class="border-b border-slate-100 dark:border-[#263449] pb-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center shadow-md">
            <Phone :size="20" />
          </div>
          <div>
            <h2 class="text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight">
              Official Contact & Campus Location
            </h2>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
              Published on website headers, footers, admissions desk widgets, and contact cards.
            </p>
          </div>
        </div>
        <button
          type="button"
          @click="saveContactDetails"
          :disabled="isSavingContact"
          class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md shadow-indigo-600/20 active:scale-95 transition disabled:opacity-50 cursor-pointer"
        >
          <Loader2 v-if="isSavingContact" :size="14" class="animate-spin" />
          <Save v-else :size="14" />
          <span>{{ isSavingContact ? "Saving..." : "Save Contact Info" }}</span>
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <!-- Primary Phone -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <Phone :size="13" class="text-slate-400" />
            <span>Primary Phone</span>
          </label>
          <input
            v-model="contactForm.primaryPhone"
            type="text"
            placeholder="+91 9876543210"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Secondary Phone -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <Phone :size="13" class="text-slate-400" />
            <span>Secondary / Helpdesk Phone</span>
          </label>
          <input
            v-model="contactForm.secondaryPhone"
            type="text"
            placeholder="+91 9876543211"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- WhatsApp Number -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <Phone :size="13" class="text-emerald-500" />
            <span>WhatsApp Admissions Chat Number</span>
          </label>
          <input
            v-model="contactForm.whatsappNumber"
            type="text"
            placeholder="+91 9876543212"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Official Email -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <Mail :size="13" class="text-slate-400" />
            <span>Official Admissions Email</span>
          </label>
          <input
            v-model="contactForm.email"
            type="email"
            placeholder="admissions@yourschool.edu"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Office Hours -->
        <div class="space-y-1.5 md:col-span-2">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <Clock :size="13" class="text-slate-400" />
            <span>Campus Office / Visiting Hours</span>
          </label>
          <input
            v-model="contactForm.officeHours"
            type="text"
            placeholder="Mon–Sat · 8:00 AM–3:00 PM"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Street Address -->
        <div class="space-y-1.5 md:col-span-2 lg:col-span-3">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
            <MapPin :size="13" class="text-rose-500" />
            <span>Full Physical Campus Address</span>
          </label>
          <input
            v-model="contactForm.address"
            type="text"
            placeholder="e.g. Udamari, Borjhargaon, Near National Highway 15"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- City -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">City / Town</label>
          <input
            v-model="contactForm.city"
            type="text"
            placeholder="e.g. Tezpur"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- District -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">District</label>
          <input
            v-model="contactForm.district"
            type="text"
            placeholder="e.g. Sonitpur"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- State -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">State / Province</label>
          <input
            v-model="contactForm.state"
            type="text"
            placeholder="e.g. Assam"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Country -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Country</label>
          <input
            v-model="contactForm.country"
            type="text"
            placeholder="India"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- PIN Code -->
        <div class="space-y-1.5">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Postal / PIN Code</label>
          <input
            v-model="contactForm.pincode"
            type="text"
            placeholder="784001"
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <!-- Google Map Link -->
        <div class="space-y-1.5 md:col-span-2 lg:col-span-3">
          <label class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Google Maps Location Link</label>
          <input
            v-model="contactForm.googleMapLink"
            type="url"
            placeholder="https://maps.google.com/..."
            class="w-full px-4 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] outline-none focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>

    <!-- CONFIRMATION MODAL -->
    <transition name="modal-fade">
      <div
        v-if="confirmModal"
        class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
        @click.self="closeModal"
      >
        <div
          class="bg-white dark:bg-[#111C30] rounded-3xl w-full max-w-md p-6 relative shadow-2xl transition-all border-t-4"
          :class="
            actionType === 'website' && website?.websiteStatus === 'active'
              ? 'border-rose-600'
              : 'border-emerald-600'
          "
        >
          <button
            @click="closeModal"
            class="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100 dark:bg-[#18243A] text-slate-400 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-[#1F2E47] hover:text-slate-700 dark:hover:text-[#F1F5F9] transition"
          >
            <X :size="15" />
          </button>

          <div class="flex justify-center mb-4">
            <div
              v-if="
                actionType === 'website' && website?.websiteStatus === 'active'
              "
              class="w-14 h-14 rounded-2xl flex items-center justify-center bg-rose-50 dark:bg-rose-950/40 text-rose-600"
            >
              <TriangleAlert :size="26" />
            </div>
            <div
              v-else
              class="w-14 h-14 rounded-2xl flex items-center justify-center bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600"
            >
              <CheckCircle2 :size="26" />
            </div>
          </div>

          <div class="text-center mb-6 space-y-2">
            <h3 class="text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">Confirm Action</h3>
            <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
              {{
                actionType === "domain"
                  ? "Are you sure you want to connect this domain? Ensure your DNS settings are configured properly at your registrar."
                  : website?.websiteStatus === "active"
                  ? "Are you sure you want to turn off your website? Visitors will temporarily lose access to your public pages."
                  : "Are you ready to turn your website back online for all users?"
              }}
            </p>
          </div>

          <div class="flex gap-3">
            <button
              @click="closeModal"
              class="flex-1 h-11 rounded-xl text-xs font-bold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-200 dark:hover:bg-[#1F2E47] transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              @click="confirmAction"
              class="flex-1 h-11 rounded-xl text-xs font-bold text-white transition active:scale-95 cursor-pointer shadow-md"
              :class="
                actionType === 'website' && website?.websiteStatus === 'active'
                  ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/20'
                  : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20'
              "
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;
    transform: scale(0.96) translateY(4px);
  }
</style>
